/**
 * Lazy loader for the Daily SDK (~85KB gzip — half the widget bundle).
 *
 * The SDK is only needed once a visitor actually starts a voice/video
 * call, so it ships as a SEPARATE ESM chunk (`daily.js`, built by
 * esbuild.config.mjs next to app.js) and loads via native dynamic
 * `import()` on first use. The import URL is computed at runtime from
 * the widget script's own directory, so esbuild cannot statically
 * resolve it and leaves the `import()` native — which classic scripts
 * are allowed to call in every browser we support.
 *
 * Callers await `loadDaily()` inside already-async join paths; kicking
 * it off in parallel with the voice-token mint hides the fetch behind
 * a network round-trip that was already happening.
 */

import type DailyIframe from '@daily-co/daily-js';
import type { DailyCall } from '@daily-co/daily-js';

export type DailyStatic = typeof DailyIframe;

let chunkBase: string | null = null;
let cached: Promise<DailyStatic> | null = null;

/** Set once at boot from config.assetBaseUrl (the app.js directory). */
export function setDailyChunkBase(base: string): void {
  chunkBase = base.replace(/\/$/, '');
}

export function loadDaily(): Promise<DailyStatic> {
  if (!cached) {
    if (!chunkBase) {
      return Promise.reject(
        new Error('[showpilot] daily chunk base not configured before voice start'),
      );
    }
    const url = `${chunkBase}/daily.js`;
    cached = import(/* @vite-ignore */ url)
      .then((m: { default?: DailyStatic }) => {
        const mod = (m.default ?? m) as DailyStatic;
        if (typeof mod.createCallObject !== 'function') {
          throw new Error('[showpilot] daily chunk loaded but SDK shape is wrong');
        }
        return mod;
      })
      .catch((err: unknown) => {
        // Reset so a later attempt can retry (transient network failure
        // must not permanently kill voice for the session).
        cached = null;
        throw err;
      });
  }
  return cached;
}

let loaded = false;
let preloadedCall: DailyCall | null = null;

/** The call-object options every session uses — preload must match. */
export const CALL_OBJECT_OPTIONS = {
  audioSource: true,
  videoSource: false,
  subscribeToTracksAutomatically: true,
} as const;

/**
 * Warm the voice runtime before the visitor asks for it (2026-10-30).
 *
 * The click-to-join wait on showpilot.com measured 2–10 s and most of it
 * was fetching and booting: this chunk (85 KB gzip), Daily's 1.8 MB
 * call-machine bundle (daily-chunk-entry.ts prefetches it), and the SDK
 * evaluating that bundle (~1.6 s measured between the token and Daily's
 * room check). `load()` with no room does the last part ahead of time —
 * 89 ms once the bundle is cached — so the click only has to join. The
 * loaded call object is handed to the first session (Daily allows one
 * instance). Called once at mount for voice-enabled agents, after the
 * page has settled; a failure here costs nothing — the click path loads
 * on its own.
 */
export function preloadVoiceRuntime(): void {
  if (!chunkBase || cached) return;
  const run = (): void => {
    loadDaily()
      .then(async (Daily) => {
        loaded = true;
        const call = Daily.createCallObject(CALL_OBJECT_OPTIONS);
        await call.load();
        preloadedCall = call;
      })
      .catch(() => {});
  };
  const w = globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 1500);
}

/** The warmed call object, once; null when none is ready. */
export function takePreloadedCall(): DailyCall | null {
  const call = preloadedCall;
  preloadedCall = null;
  return call;
}

/** True when the chunk was already in memory before the call started. */
export function isDailyPreloaded(): boolean {
  return loaded;
}

/** Test hook — reset module state between cases. */
export function resetDailyLoaderForTests(): void {
  chunkBase = null;
  cached = null;
  loaded = false;
  preloadedCall = null;
}
